import React, { useCallback, useEffect, useState } from "react";
import { Plus, FileDown, Pencil, Trash2, Paperclip } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import DocumentsDialog from "../../components/DocumentsDialog";
import { PageHead, Field, Empty, PortPicker, usePorts, CONTAINERS, CARGOS, CURRENCIES, money, pdfUrl } from "./common";

const STATUS_STYLE = {
  New: "bg-sky-100 text-sky-800 border-sky-200",
  "Under Review": "bg-amber-100 text-amber-800 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
  Expired: "bg-slate-100 text-slate-600 border-slate-200",
};

const BLANK = {
  provider_name: "", contact_details: "", origin_port_id: "", destination_port_id: "",
  container_type: "20FT", cargo_type: "General", ocean_freight: "", additional_charges: "",
  currency: "USD", transit_days: "", quotation_date: "", validity_date: "", remarks: "",
  status: "New", save_as_rate: true,
};

export default function FreightQuotations() {
  const { user } = useAuth();
  const ports = usePorts();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [docsRow, setDocsRow] = useState(null);
  const canCreate = hasPerm(user, "sea_freight", "create");
  const canEdit = hasPerm(user, "sea_freight", "edit");
  const canDelete = hasPerm(user, "sea_freight", "delete");
  const canApprove = hasPerm(user, "sea_freight", "approve");

  const load = useCallback(() => {
    api.get("/sea-freight/quotations", { params: { status } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...BLANK, ...row, save_as_rate: false });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form };
    const o = ports.find((p) => p.id === payload.origin_port_id);
    const d = ports.find((p) => p.id === payload.destination_port_id);
    payload.origin_port = o?.port_name || payload.origin_port || "";
    payload.destination_port = d?.port_name || payload.destination_port || "";
    try {
      if (editing) await api.put(`/sea-freight/quotations/${editing.id}`, payload);
      else await api.post("/sea-freight/quotations", payload);
      toast.success(editing ? "Quotation updated" : "Quotation saved");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setStatusOf = async (row, newStatus) => {
    try {
      await api.put(`/sea-freight/quotations/${row.id}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/sea-freight/quotations/${id}`); toast.success("Quotation removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="freight-quotations-page">
      <PageHead title="Freight Quotations"
        subtitle="Actual quotations received from freight forwarders and shipping lines, with attachments. Approved quotations also feed the rate comparison as Forwarder Quoted rates."
        right={<>
          <Button data-testid="export-quotes-pdf" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(pdfUrl("/sea-freight/export/quotations.pdf", { status }), "_blank")}>
            <FileDown className="w-4 h-4" /> Comparison PDF
          </Button>
          {canCreate && <Button data-testid="add-quote-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={openNew}><Plus className="w-4 h-4" /> Add quotation</Button>}
        </>} />

      <div className="mb-4 max-w-xs">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="quote-status-filter" className="bg-white h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{(data?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data && data.quotations.length === 0 ? <Empty testid="quotes-empty">No freight quotations recorded yet.</Empty> : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="quotes-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["Date", "Provider", "Lane", "Container", "Ocean", "Additional", "Total", "Transit", "Valid until", "Status", "Files", "Actions"].map((h) => (
                <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody>
              {(data?.quotations || []).map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{q.quotation_date}</td>
                  <td className="px-3 py-2.5 text-slate-800 whitespace-nowrap">{q.provider_name}<span className="block text-[11px] text-slate-400">{q.contact_details}</span></td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{q.origin_port} → {q.destination_port}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{q.container_type} / {q.cargo_type}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(q.ocean_freight, q.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(q.additional_charges, q.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-slate-900">{money(q.total_freight, q.currency)}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{q.transit_days ? `${q.transit_days} d` : "-"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{q.validity_date || "-"}
                    {q.expired && <Badge variant="outline" data-testid={`quote-expired-${q.id}`} className="ml-1 rounded-full text-[10px] px-1.5 bg-red-100 text-red-700 border-red-200">expired</Badge>}</td>
                  <td className="px-3 py-2.5"><Badge variant="outline" className={`rounded-full px-2 text-[11px] ${STATUS_STYLE[q.status] || ""}`}>{q.status}</Badge></td>
                  <td className="px-3 py-2.5">
                    <Button data-testid={`quote-docs-${q.id}`} size="sm" variant="ghost" className="h-8 rounded-sm gap-1 text-slate-500 hover:text-[#0B132B]" onClick={() => setDocsRow(q)}>
                      <Paperclip className="w-3.5 h-3.5" /> {q.attachment_count || 0}
                    </Button>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-0.5">
                      {canApprove && q.status !== "Approved" && <Button data-testid={`approve-quote-${q.id}`} size="sm" variant="ghost" className="h-8 rounded-sm text-emerald-700 hover:bg-emerald-50" onClick={() => setStatusOf(q, "Approved")}>Approve</Button>}
                      {canApprove && q.status !== "Rejected" && <Button data-testid={`reject-quote-${q.id}`} size="sm" variant="ghost" className="h-8 rounded-sm text-red-600 hover:bg-red-50" onClick={() => setStatusOf(q, "Rejected")}>Reject</Button>}
                      {canEdit && <Button data-testid={`edit-quote-${q.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" onClick={() => openEdit(q)}><Pencil className="w-4 h-4" /></Button>}
                      {canDelete && <Button data-testid={`delete-quote-${q.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(q.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Edit freight quotation" : "Add freight quotation"}</DialogTitle>
            <DialogDescription>Enter the quotation exactly as received from the forwarder. Attach the PDF after saving.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Provider name"><Input data-testid="quote-provider" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className="bg-white h-9" /></Field>
            <div className="sm:col-span-2"><Field label="Contact details"><Input data-testid="quote-contact" value={form.contact_details} onChange={(e) => setForm({ ...form, contact_details: e.target.value })} placeholder="Name, phone, email" className="bg-white h-9" /></Field></div>
            <Field label="Origin port"><PortPicker testid="quote-origin" ports={ports} value={form.origin_port_id} onChange={(v) => setForm({ ...form, origin_port_id: v })} /></Field>
            <Field label="Destination port"><PortPicker testid="quote-destination" ports={ports} value={form.destination_port_id} onChange={(v) => setForm({ ...form, destination_port_id: v })} /></Field>
            <Field label="Container type">
              <Select value={form.container_type} onValueChange={(v) => setForm({ ...form, container_type: v })}>
                <SelectTrigger data-testid="quote-container" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTAINERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Cargo type">
              <Select value={form.cargo_type} onValueChange={(v) => setForm({ ...form, cargo_type: v })}>
                <SelectTrigger data-testid="quote-cargo" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CARGOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Ocean freight"><Input data-testid="quote-ocean" type="number" min="0" value={form.ocean_freight} onChange={(e) => setForm({ ...form, ocean_freight: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Additional charges"><Input data-testid="quote-additional" type="number" min="0" value={form.additional_charges} onChange={(e) => setForm({ ...form, additional_charges: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="quote-currency" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Transit time (days)"><Input data-testid="quote-transit" type="number" min="0" value={form.transit_days || ""} onChange={(e) => setForm({ ...form, transit_days: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Quotation date"><Input data-testid="quote-date" type="date" value={form.quotation_date} onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Validity date"><Input data-testid="quote-validity" type="date" value={form.validity_date} onChange={(e) => setForm({ ...form, validity_date: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="quote-status" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{(data?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2"><Field label="Remarks"><Input data-testid="quote-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="bg-white h-9" /></Field></div>
            {!editing && (
              <div className="sm:col-span-3 flex items-center gap-2 text-sm text-slate-600">
                <input data-testid="quote-save-as-rate" type="checkbox" checked={form.save_as_rate} onChange={(e) => setForm({ ...form, save_as_rate: e.target.checked })} />
                Also record this as a Forwarder Quoted rate in the rate comparison
              </div>
            )}
          </div>
          {editing && (
            <div className="border-t border-slate-200 pt-3 mt-2">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Attachments</h3>
              <Button variant="outline" className="rounded-sm gap-1.5" data-testid="quote-open-docs" onClick={() => { setOpen(false); setDocsRow(editing); }}>
                <Paperclip className="w-4 h-4" /> Manage attachments
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-quote-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>{editing ? "Save changes" : "Save quotation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {docsRow && <DocumentsDialog recordType="freight_quotation" recordId={docsRow.id}
        title={`${docsRow.provider_name} · ${docsRow.origin_port} → ${docsRow.destination_port}`}
        onClose={() => { setDocsRow(null); load(); }} />}
    </div>
  );
}
